package br.com.vvrefeicoes.portal.feature.guides

import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInWindow
import kotlinx.browser.document
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLIFrameElement
import org.w3c.dom.HTMLVideoElement

@Composable
internal actual fun PlatformPdfViewer(
    url: String,
    fileName: String,
    scrollOffset: Int,
    visible: Boolean,
    modifier: Modifier,
) {
    var coordinates by remember { mutableStateOf<LayoutCoordinates?>(null) }
    val wrapper = remember(url) { createWrapper() }
    val frame = remember(url) { createFrame(url) }

    DisposableEffect(url, visible) {
        if (visible) {
            wrapper.appendChild(frame)
            document.body?.appendChild(wrapper)
        }
        onDispose {
            runCatching { document.body?.removeChild(wrapper) }
        }
    }
    LaunchedEffect(coordinates, scrollOffset, url, visible) {
        if (visible) applyBounds(wrapper, coordinates)
    }
    Box(modifier = modifier.onGloballyPositioned { coordinates = it })
}

@Composable
internal actual fun PlatformVideoPlayer(
    url: String,
    caption: String,
    scrollOffset: Int,
    visible: Boolean,
    modifier: Modifier,
) {
    var coordinates by remember { mutableStateOf<LayoutCoordinates?>(null) }
    val wrapper = remember(url) { createWrapper() }
    val video = remember(url) { createVideo(url) }

    DisposableEffect(url, visible) {
        if (visible) {
            wrapper.appendChild(video)
            document.body?.appendChild(wrapper)
        }
        onDispose {
            runCatching {
                if (!visible) video.pause()
                document.body?.removeChild(wrapper)
            }
        }
    }
    LaunchedEffect(coordinates, scrollOffset, url, visible) {
        if (visible) applyBounds(wrapper, coordinates)
    }
    Box(modifier = modifier.onGloballyPositioned { coordinates = it })
}

private fun createWrapper(): HTMLDivElement {
    val wrapper = document.createElement("div") as HTMLDivElement
    wrapper.style.apply {
        position = "fixed"
        zIndex = "2147483000"
        background = "#ffffff"
        borderRadius = "12px"
        setProperty("overflow", "hidden")
        boxShadow = "0 12px 32px rgba(16, 24, 40, 0.18)"
        border = "1px solid #d0d5dd"
    }
    return wrapper
}

private fun createFrame(url: String): HTMLIFrameElement {
    val frame = document.createElement("iframe") as HTMLIFrameElement
    frame.setAttribute("src", url)
    frame.setAttribute("title", "Visualizador de PDF")
    frame.style.apply {
        width = "100%"
        height = "100%"
        border = "0"
        display = "block"
    }
    return frame
}

private fun createVideo(url: String): HTMLVideoElement {
    val video = document.createElement("video") as HTMLVideoElement
    video.setAttribute("src", url)
    video.setAttribute("controls", "true")
    video.setAttribute("playsinline", "true")
    video.setAttribute("preload", "metadata")
    video.style.apply {
        width = "100%"
        height = "100%"
        display = "block"
        background = "#000000"
        setProperty("object-fit", "contain")
    }
    return video
}

private fun applyBounds(
    wrapper: HTMLDivElement,
    coordinates: LayoutCoordinates?,
) {
    if (coordinates == null) return
    val position = coordinates.positionInWindow()
    val size = coordinates.size
    wrapper.style.apply {
        left = "${position.x}px"
        top = "${position.y}px"
        width = "${size.width}px"
        height = "${size.height}px"
        display = if (size.width <= 0 || size.height <= 0) "none" else "block"
    }
}
